/**
 * Rows of objects to a CSV string, and a download (#316).
 *
 * WHY CLIENT-SIDE, when services/exportApi.js does its CSVs server-side.
 * Those are whole-entity dumps -- every hive, every site -- which are
 * naturally one table and belong in one request. A report is not: it has
 * a summary, a breakdown by month and a breakdown by vendor, and there
 * is no honest single CSV of all three. What somebody wants is "the
 * monthly figures, in a spreadsheet".
 *
 * So the CSV is per SECTION, built from data the browser already has
 * after generating the report. A server round trip would re-run the
 * query to hand back a slice of what was just sent.
 *
 * WHY NOT A LIBRARY. papaparse is already a dependency of the admin
 * console and adding it here would be defensible -- but this is eight
 * lines of exactly-specified escaping (RFC 4180), and this codebase has
 * refused dependencies for less. The escaping is the only part that can
 * be wrong, so it is the part with tests.
 */

/**
 * One CSV field.
 *
 * Quoted only when it has to be, which keeps the common case readable
 * when somebody opens the file in a text editor. A field needs quoting
 * if it contains a comma, a quote, or a newline -- and a quote inside a
 * quoted field is doubled, not backslash-escaped, which is the mistake
 * that makes Excel swallow the rest of the row.
 */
function field(value) {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (!/[",\r\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

/**
 * Rows of objects to CSV text, with a header row from the first row's
 * keys.
 *
 * Column order follows the first row rather than being sorted, so the
 * spreadsheet matches the table on screen -- a report whose columns
 * reorder between the page and the export is one somebody has to re-read
 * to trust.
 */
export function toCsv(rows) {
  if (!rows || rows.length === 0) return "";
  const columns = Object.keys(rows[0]);
  const lines = [columns.map(field).join(",")];
  for (const row of rows) {
    lines.push(columns.map((c) => field(row[c])).join(","));
  }
  // CRLF, per RFC 4180. Excel on Windows is the overwhelming consumer of
  // these files and a bare \n leaves it showing one long row in some
  // versions.
  return lines.join("\r\n");
}

/** Save a CSV to the viewer's machine. */
export function downloadCsv(rows, filename) {
  const csv = toCsv(rows);
  if (!csv) return false;
  /*
   * The BOM is deliberate. Without it Excel reads the file as the
   * system codepage rather than UTF-8, and a vendor called "Müller"
   * becomes "MÃ¼ller" -- which reads as data corruption to whoever
   * opens it, not as an encoding setting.
   */
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  } finally {
    // An un-revoked blob URL pins the whole file in memory for the life
    // of the tab.
    URL.revokeObjectURL(url);
  }
  return true;
}
